import {
  useQueryClient,
} from '@tanstack/react-query';
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import isNil from 'lodash/isNil';
import {
  InsuranceDTO,
  SyncInsuranceAttachmentsRequestDTO,
} from 'dtos';
import dayjs from 'utils/dayjs';
import {
  getSubmitButtonText,
  sanitizeFileName,
} from '@populateui/utils';
import {
  useConfirmDialog,
  useNotify,
} from 'core/hooks';
import Button from 'pages/Dashboard/components/Button';
import Dialog from 'pages/Dashboard/components/Dialog';
import {
  useGetPatientSummary,
  useGetPatientInsurances,
  useInsuranceMetaData,
  useSyncInsuranceAttachments,
  useUpsertInsurances,
} from 'views/EMR/components/Insurance/hooks';
import {
  getInsuranceSlotConflictValidator,
} from 'utils/misc';
import InsuranceForm, {
  InsuranceDataForm,
} from 'views/EMR/components/Insurance/Dialog/InsuranceForm';
import AssociatedInsuranceCardsPanel from 'views/EMR/components/Insurance/Dialog/AssociatedInsuranceCardsPanel';
import {
  getDemographicsFromInsurance,
  getPatientDemographics,
} from 'pages/Dashboard/utils/patient';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import {
  serializeInsurance,
} from 'pages/Dashboard/utils/helper';
import {
  insurancesEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  MultimediaType,
} from 'pages/Dashboard/utils/constants';
import {
  InsuranceAttachmentFormState,
} from 'views/EMR/components/Insurance/Dialog/AssociatedInsuranceCardsPanel/useAssociatedInsuranceCards';
import omit from 'lodash/omit';

export type InsuranceDialogParams = {
  patientId: number;
  insurance?: InsuranceDTO;
  dialogHeight?: number;
};

type Props = InsuranceDialogParams & {
  open: boolean;
  onClose: () => void;
};

function serializeInsuranceAttachmentSyncRequest(
  insuranceId: number,
  patientId: number,
  attachmentState: InsuranceAttachmentFormState,
): SyncInsuranceAttachmentsRequestDTO {
  const mediaList = attachmentState.newUploadFiles.map(({ dataUrl, file, name }) => ({
    multimediaType: MultimediaType.InsuranceCard,
    dataUrl: dataUrl ?? '',
    fileName: sanitizeFileName(file?.name ?? name ?? ''),
  }));

  return {
    insuranceId,
    ensureAssociatedPatientMediaIds: attachmentState.existingMediaIds,
    newUploads: mediaList.length > 0
      ? {
        patientId,
        mediaList,
      }
      : undefined,
  };
}

export default function InsuranceDialog({
  insurance,
  open,
  onClose,
  dialogHeight,
  patientId,
}: Props) {
  const {
    data: patientSummary,
    isLoading: isLoadingPatientSummary,
  } = useGetPatientSummary(patientId ?? 0);

  const {
    data: patientInsurances,
  } = useGetPatientInsurances(patientId ?? 0);

  const queryClient = useQueryClient();
  const [savedInsurance, setSavedInsurance] = useState(insurance);
  const [hasAttachmentError, setHasAttachmentError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const policySavedRef = useRef(false);
  const savedInsuranceRef = useRef(insurance);
  const pendingAttachmentsRef = useRef<SyncInsuranceAttachmentsRequestDTO[]>([]);
  const isNewInsurance = isNil(savedInsurance);
  const { mutateAsync: upsertInsurances } = useUpsertInsurances();
  const { mutateAsync: syncInsuranceAttachments } = useSyncInsuranceAttachments();
  const notify = useNotify();
  const { confirm, closeConfirm } = useConfirmDialog();

  const defaultInsuranceOwnerType = isNewInsurance
    ? 'Self' : savedInsurance?.insuranceOwnerRelationshipType;

  const {
    relationshipTypesByName,
    restrictedSlotSet,
    getInsuranceSlotLabel,
  } = useInsuranceMetaData();

  const validateInsuranceSlotConflict = useMemo(
    () => getInsuranceSlotConflictValidator(
      patientInsurances ?? [],
      restrictedSlotSet,
    ),
    [patientInsurances, restrictedSlotSet],
  );

  const {
    isSubscriberPatient,
    isEmployerInsurance,
  } = relationshipTypesByName[defaultInsuranceOwnerType ?? ''] ?? {};

  const defaultData = {
    ...insurance,
    attachmentState: {
      existingMediaIds: [],
      newUploadFiles: [],
    },
    insuranceOwnerRelationshipType: defaultInsuranceOwnerType,
    insuranceOwnerDOB: dayjs(insurance?.insuranceOwnerDOB),
    effectiveFrom: dayjs(insurance?.effectiveFrom ?? undefined),
    effectiveTill: isNil(insurance?.effectiveTill) ? null : dayjs(insurance?.effectiveTill),
    demographics: isSubscriberPatient || isEmployerInsurance
      ? getPatientDemographics(patientSummary ?? {})
      : getDemographicsFromInsurance(insurance ?? {}),
  } as InsuranceDataForm;

  const formProps = useForm<InsuranceDataForm>({
    mode: 'onChange',
    defaultValues: defaultData,
  });

  const {
    formState: { isValid, isSubmitting },
    handleSubmit,
    setValue,
  } = formProps;

  useEffect(() => {
    if (!isNil(patientSummary) && (isSubscriberPatient || isEmployerInsurance)) {
      setValue('demographics', getPatientDemographics(patientSummary ?? {}));
    }
  }, [patientSummary, isSubscriberPatient, isEmployerInsurance]);

  const onSave = async (formData: InsuranceDataForm) => {
    try {
      const conflictingInsurance = validateInsuranceSlotConflict({
        currentInsuranceId: formData?.insuranceId,
        selectedInsuranceSlot: formData?.insuranceSlot,
        isActiveInsurance: !(formData?.inactive ?? false),
      });

      const saveInsurance = async () => {
        if (savingRef.current) return;
        savingRef.current = true;
        setIsSaving(true);
        try {
          if (pendingAttachmentsRef.current.length === 0) {
            const insuranceOwnerRelationshipType = relationshipTypesByName[formData.insuranceOwnerRelationshipType ?? ''];
            const insuranceId = savedInsuranceRef.current?.insuranceId ?? formData.insuranceId;
            const insurance = serializeInsurance(
              {
                ...omit(formData, 'insuranceId'),
                ...(isNil(insuranceId) ? {} : { insuranceId }),
              },
              patientSummary ?? {},
              insuranceOwnerRelationshipType,
            );
            const updatedInsurance = await upsertInsurances({ patientId, insurance });
            const savedInsuranceId = updatedInsurance?.insuranceId ?? 0;
            if (savedInsuranceId <= 0) throw new Error('Unable to resolve saved insurance ID');

            savedInsuranceRef.current = updatedInsurance;
            policySavedRef.current = true;
            setSavedInsurance(updatedInsurance);
            setValue('insuranceId', savedInsuranceId);
            // The policy is already saved, regardless of the attachment outcome.
            insurancesEventChannel.emit('onPatientInsuranceUpdated', patientId, updatedInsurance);

            const payload = serializeInsuranceAttachmentSyncRequest(savedInsuranceId, patientId, formData.attachmentState);
            const uploads = payload.newUploads?.mediaList ?? [];
            // Keep acknowledged uploads out of subsequent retries.
            pendingAttachmentsRef.current = uploads.length === 0 ? [payload] : uploads.map((media, index) => ({
              ...payload,
              deletePatientMediaIdsNotInList: index === 0 ? payload.deletePatientMediaIdsNotInList : false,
              newUploads: { patientId, mediaList: [media] },
            }));
          }

          try {
            /* eslint-disable no-await-in-loop -- retry only unacknowledged card uploads */
            while (pendingAttachmentsRef.current.length > 0) {
              await syncInsuranceAttachments(pendingAttachmentsRef.current[0]);
              pendingAttachmentsRef.current.shift();
            }
            /* eslint-enable no-await-in-loop */
          } catch (error) {
            setHasAttachmentError(true);
            notify({ variant: 'error', message: 'Insurance saved. Some cards could not be uploaded. Retry the card upload or close this window.' });
            return;
          }

          setHasAttachmentError(false);
          notify({ variant: 'success', message: 'Insurance and associated cards saved successfully' });
          onClose();
        } catch (error) {
          notify({ variant: 'error', message: 'Insurance could not be saved. Check the information and try again.' });
          console.error(error);
        } finally {
          const insuranceId = savedInsuranceRef.current?.insuranceId;
          if (insuranceId && policySavedRef.current) {
            Promise.all([
              queryClient.invalidateQueries({ queryKey: ['insurance-attachments', insuranceId] }),
              queryClient.invalidateQueries({ queryKey: ['unassociated-insurance-cards', patientId] }),
            ]).catch((error) => {
              console.error('Unable to refresh saved insurance cards:', error);
            });
          }
          savingRef.current = false;
          setIsSaving(false);
        }
      };

      if (pendingAttachmentsRef.current.length > 0) {
        await saveInsurance();
        return;
      }

      if (!isNil(conflictingInsurance)) {
        const slotLabel = !isNil(formData?.insuranceSlot)
          ? getInsuranceSlotLabel(formData.insuranceSlot)
          : undefined;
        confirm({
          content: (
            <div>
              Are you sure you want to mark
              {' '}
              <span className="font-semibold">{formData?.insuranceName}</span>
              {' '}
              as
              {' '}
              <span className="font-semibold">{`Active ${slotLabel}`}</span>
              ?
              <div className="mt-2">
                <span className="font-semibold">{conflictingInsurance.insuranceName}</span>
                {' is currently set as '}
                <span className="font-semibold">{slotLabel}</span>
                {' and will be moved to '}
                <span className="font-semibold">Other</span>
                .
              </div>
            </div>
          ),
          onConfirm: async () => {
            try {
              await saveInsurance();
              closeConfirm();
            } catch (error) {
              console.error(error);
            }
          },
          title: isNewInsurance ? 'Add Insurance' : 'Update Insurance',
          confirmText: 'Confirm',
        });
        return;
      }

      await saveInsurance();
    } catch (error) {
      console.error(error);
    }
  };

  const attachmentSubmitText = isSaving ? 'Uploading...' : 'Retry Card Uploads';
  const submitButtonText = hasAttachmentError
    ? attachmentSubmitText
    : getSubmitButtonText(isNewInsurance, isSubmitting || isSaving);

  return (
    <Dialog
      open={open}
      onClose={() => { if (!savingRef.current) onClose(); }}
      className="min-w-[40rem] md:w-[44rem] lg:!w-[64rem] xl:!w-[72rem] transition-all duration-300 !space-y-0 overflow-y-hidden !p-0"
      titleClassName="p-5"
      title={isNewInsurance ? 'Add Insurance' : 'Update Insurance'}
      minHeight={dialogHeight}
    >
      <FormProvider {...formProps}>
        <div className="flex min-h-0 flex-1 flex-col max-h-[80vh]">
          {hasAttachmentError && (
            <p role="alert" className="px-5 text-sm text-amber-700">
              Insurance saved. Some cards could not be uploaded. Retry the card upload or close this window; the policy will remain saved.
            </p>
          )}
          <fieldset disabled={isSaving || hasAttachmentError} className="contents">
            <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overflow-x-hidden px-5 lg:flex-row lg:items-stretch">
              <div className="flex min-w-0 w-full flex-1">
                <InsuranceForm
                  patientSummary={patientSummary ?? {}}
                  defaultValues={defaultData}
                  isLoading={isLoadingPatientSummary}
                />
              </div>
              <AssociatedInsuranceCardsPanel
                className="h-[16rem] shrink-0 lg:h-auto lg:min-h-0 lg:shrink lg:self-stretch"
                insuranceId={savedInsurance?.insuranceId}
                patientId={patientId}
                open={open}
              />
            </div>
          </fieldset>
          <div className="flex shrink-0 flex-row-reverse justify-between p-5">
            <DisabledFormTooltip>
              <Button
                variant="contained"
                color="primary"
                disabled={(!hasAttachmentError && !isValid) || isSubmitting || isSaving}
                onClick={hasAttachmentError ? () => onSave(formProps.getValues()) : handleSubmit(onSave)}
              >
                {submitButtonText}
              </Button>
            </DisabledFormTooltip>
            <Button
              variant="outlined"
              disabled={isSaving}
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </FormProvider>
    </Dialog>
  );
}
